import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, switchMap, tap } from 'rxjs';
import { DatePipe } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import {
  ApiResponse,
  Meta,
} from '../../../../common/models/api-response.model';
import { RequestDetailsResponse } from '../../../../common/models/request-details.model';
import { RequestService } from '../../../../common/services/request/request.service';
import {
  AccessKeyResponse,
  WorkflowAccessKeyService,
} from '../../../../common/services/workflow-access-key/workflow-access-key.service';
import { AppStorage } from '../../../../common/enums/app-storage';
import { RequestDetailsHeaderComponent } from '../../../../common/components/request-details-header/request-details-header.component';
import { HorizontalTreeComponent } from '../../../../common/components/horizontal-tree/horizontal-tree.component';
import { TreeNode } from '../../../../common/components/horizontal-tree/horizontal-tree.model';
import { AvatarComponent } from '../../../../../ui-components/lib/avatar/avatar.component';
import { TrimTooltipDirective } from '../../../../../ui-components/directives/trim-tooltip/trim-tooltip.directive';
import { BadgeComponent } from '../../../../../ui-components/lib/badge/badge.component';
import { StepStatusPipe } from '../../../../common/pipes/step-status/step-status.pipe';
import { SequentialRequestService } from '../services/sequential-request/sequential-request.service';
import { SequentialWfItem } from '../../../../common/models/sequential-request.model';

@Component({
  selector: 'lib-sequential-request',
  imports: [
    HorizontalTreeComponent,
    AvatarComponent,
    TrimTooltipDirective,
    DatePipe,
    BadgeComponent,
    StepStatusPipe,
    RequestDetailsHeaderComponent,
  ],
  templateUrl: './sequential-request.component.html',
  styleUrl: './sequential-request.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SequentialRequestComponent implements OnInit {
  private readonly sequentialRequestService = inject(SequentialRequestService);
  private readonly requestService = inject(RequestService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly workflowAccessKeyService = inject(WorkflowAccessKeyService);
  private readonly destroyRef = inject(DestroyRef);

  detailsTemplate = viewChild<TemplateRef<unknown>>('detailsTemplate');

  flowId = signal<number>(this.activatedRoute.snapshot.params['flowId'] || 0);
  requestDetailsResponse = signal<RequestDetailsResponse>({
    data: {
      id: 0,
      name: '',
      description: '',
      category: { id: 0, name: '' },
      current_position: 0,
      icon: { id: 0, name: '', mime_type: '', url: '', order: 0 },
      state: { value: '', label: '' },
      requirements: [],
      permissions: [],
      positions: [],
      creator: { id: 0, name: '' },
      sub_flow_templates_exists: false,
      is_triggable: false,
    },
  });
  isLoading = signal<boolean>(false);

  levels = signal<number[]>([]);
  nodesPerLevel = signal<Record<number, TreeNode[]>>({});
  isLoadingPerLevel = signal<Record<number, boolean>>({});
  perPage = signal<number>(50);
  isNoneSelected = signal<Record<number, boolean>>({});

  sequentialPagination = signal<Record<string, Meta>>({});
  sequentialCurrentPages = signal<Record<string, number>>({});
  canLoadMoreSequential = signal<Record<string, boolean>>({});

  ngOnInit(): void {
    this.initFlowData();
    this.getSequentialWfTree(this.flowId());
  }

  initFlowData(): void {
    if (!this.flowId()) return;

    this.isLoading.set(true);
    this.workflowAccessKeyService
      .getFlowAuthorizationKey(this.flowId())
      .pipe(
        tap((res: AccessKeyResponse) =>
          sessionStorage.setItem(AppStorage.accessKey, res.data.access_key)
        ),
        switchMap(() =>
          this.requestService.getFlowDetails(this.flowId().toString())
        ),
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: res => this.setFlowDetails(res),
      });
  }

  getSequentialWfTree(
    containerId: number,
    parentIndex = 0,
    parentLevel = 0,
    loadMore = false
  ): void {
    const targetLevel = parentLevel + 1;
    const levelKey = `${parentLevel}`;

    if (!loadMore) this.initLevelsState(containerId, parentLevel, targetLevel);

    this.setLoadingForLevel(parentLevel > 0 ? targetLevel : 0, true);

    const page = this.resolvePage(levelKey, loadMore);

    this.sequentialRequestService
      .getSequentialWf(this.perPage(), page, containerId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(response =>
        this.handleSequentialWfTreeResponse(
          response,
          parentLevel,
          parentIndex,
          levelKey,
          parentLevel > 0 ? containerId : undefined,
          loadMore
        )
      );
  }

  handleSequentialWfTreeResponse(
    response: ApiResponse<SequentialWfItem>,
    parentLevel: number,
    parentIndex: number,
    levelKey: string,
    containerId?: number,
    loadMore = false
  ): void {
    const { data: workflows, meta } = response;
    const targetLevel = containerId ? parentLevel + 1 : parentLevel;

    this.updatePaginationState(levelKey, meta);

    if (workflows.length) {
      const newNodes = this.setSequentialWfNodeList(
        workflows,
        parentLevel,
        parentIndex,
        containerId?.toString() ?? '',
        loadMore
      );

      const targetLevelNodes = this.mergeNodesAtLevel(targetLevel, newNodes, loadMore);

      const hasSelection = targetLevelNodes.some(node => node.isSelected);
      this.isNoneSelected.set({
        ...this.isNoneSelected(),
        [targetLevel]: targetLevelNodes.length === 0 || !hasSelection,
      });

      if (!containerId && !parentLevel && !loadMore && targetLevelNodes[0]?.count) {
        this.autoFetchFirstNode(workflows, targetLevelNodes);
      }
    }

    this.setLoadingForLevel(containerId ? parentLevel + 1 : parentLevel, false);
  }

  fetchByDetailsAvailability(
    hasDetails: boolean,
    flowTemplateId: number,
    parentIndex = 0,
    parentLevel = 0
  ): void {
    const targetLevel = parentLevel + 1;
    if (!this.levels().includes(targetLevel)) {
      this.levels.set([...this.levels(), targetLevel]);
    }
    this.setLoadingForLevel(targetLevel, true);
    const levelKey = `${parentLevel}`;
    this.resolvePage(levelKey, false);

    this.resolveSequentialService$(hasDetails, flowTemplateId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(response =>
        this.handleSequentialWfTreeResponse(
          response,
          parentLevel,
          parentIndex,
          levelKey,
          flowTemplateId
        )
      );
  }

  setSequentialWfNodeList(
    workflows: SequentialWfItem[],
    lastLevel: number,
    parentIndex: number | null,
    parentId: string,
    loadMore = false
  ): TreeNode[] {
    const nodeLevel = parentId ? lastLevel + 1 : lastLevel;
    const indexOffset = loadMore
      ? this.getExistingNodeCountAtLevel(nodeLevel, parentId)
      : 0;

    return workflows.map((item, index) =>
      this.buildTreeNode(item, nodeLevel, index + indexOffset, parentId, parentIndex)
    );
  }

  onCardClick(node: TreeNode): void {
    const current = this.nodesPerLevel();
    this.nodesPerLevel.set({
      ...current,
      [node.level]: current[node.level].map(
        item => ({ ...item, isSelected: item.id === node.id })
      ),
    });

    this.isNoneSelected.set({ ...this.isNoneSelected(), [node.level]: false });

    if (node.count === 0) return;

    this.clearNodesPerLevel(node.level);
    const hasDetails = !!node.details?.['hasDetails'];
    const flowTemplateId = node.details?.['flowTemplateId'];
    const detailsId = node.details?.['requestId'] || node.id;
    this.fetchByDetailsAvailability(
      hasDetails,
      hasDetails ? detailsId : flowTemplateId,
      node.index,
      node.level
    );
  }

  clearNodesPerLevel(level: number): void {
    const isAboveLevel = (key: string) => Number.parseInt(key, 10) > level;

    this.nodesPerLevel.set(
      Object.fromEntries(
        Object.entries(this.nodesPerLevel()).filter(([key]) => !isAboveLevel(key))
      )
    );
    this.isNoneSelected.set(
      Object.fromEntries(
        Object.entries(this.isNoneSelected()).filter(([key]) => !isAboveLevel(key))
      )
    );
    this.levels.set(this.levels().filter(l => l <= level));
  }

  canLoadMoreAtLevel(level: number): boolean {
    return this.canLoadMoreSequential()[level];
  }

  onLoadMoreAtLevel(level: number): void {
    if (!this.canLoadMoreSequential()[level]) return;

    if (level === 0) {
      this.getSequentialWfTree(this.flowId(), 0, 0, true);
    } else {
      const selectedNode = this.nodesPerLevel()[level]?.find(n => n.isSelected);
      if (!selectedNode) return;
      this.getSequentialWfTree(
        Number(selectedNode.id),
        selectedNode.index,
        level,
        true
      );
    }
  }

  private getExistingNodeCountAtLevel(
    level: number,
    parentId?: string
  ): number {
    const nodesAtLevel = this.nodesPerLevel()[level] || [];
    if (!parentId) return nodesAtLevel.length;
    return nodesAtLevel.filter(node => node.parentId === parentId).length;
  }

  private setLoadingForLevel(level: number, isLoading: boolean): void {
    this.isLoadingPerLevel.set({
      ...this.isLoadingPerLevel(),
      [level]: isLoading,
    });
  }

  private resolveSequentialService$(
    hasDetails: boolean,
    flowTemplateId: number
  ) {
    return hasDetails
      ? this.sequentialRequestService.getSequentialWf(this.perPage(), 1, flowTemplateId)
      : this.sequentialRequestService.getSequentialWfDetails(this.perPage(), 1, flowTemplateId);
  }

  private buildTreeNode(
    item: SequentialWfItem,
    nodeLevel: number,
    index: number,
    parentId: string,
    parentIndex: number | null
  ): TreeNode {
    const hasDetails = !!item.details && Object.keys(item.details).length > 0;
    const count = item.sub_flow_templates_count ?? 0;

    return {
      id: item.id.toString(),
      name: item.name,
      title: item.details?.state?.label || '',
      image: null,
      count,
      children: [],
      level: nodeLevel,
      index,
      isSelected: parentId ? false : index === 0,
      parentId: parentId ?? '',
      isChild: !!parentId,
      parentIndex: parentId ? parentIndex : null,
      viewRoute:
        hasDetails && count === 0
          ? `/requests/request/view/${item.details?.id}`
          : undefined,
      details: {
        id: item.id,
        requestId: item.details?.request_id || '',
        name: item.name,
        state: item.details?.state || null,
        category: item.details?.category?.name || '',
        currentPositionName: item.details?.current_position_name || '',
        dueDate: item.details?.due_date || null,
        currentAssignees: item.details?.current_assignees || [],
        flowTemplateId: item.flow_template_id,
        hasDetails,
      },
      detailsTemplate: this.detailsTemplate(),
    };
  }

  private updatePaginationState(levelKey: string, meta: Meta): void {
    this.sequentialPagination.set({
      ...this.sequentialPagination(),
      [levelKey]: meta,
    });
    this.sequentialCurrentPages.set({
      ...this.sequentialCurrentPages(),
      [levelKey]: meta.current_page,
    });
    this.canLoadMoreSequential.set({
      ...this.canLoadMoreSequential(),
      [levelKey]: meta.current_page < meta.last_page,
    });
  }

  private mergeNodesAtLevel(
    targetLevel: number,
    newNodes: TreeNode[],
    loadMore: boolean
  ): TreeNode[] {
    const current = this.nodesPerLevel();
    const existing = current[targetLevel] ?? [];
    const merged = loadMore && existing.length
      ? [...existing, ...newNodes]
      : newNodes;
    this.nodesPerLevel.set({ ...current, [targetLevel]: merged });
    return merged;
  }

  private autoFetchFirstNode(
    workflows: SequentialWfItem[],
    targetLevelNodes: TreeNode[]
  ): void {
    const firstWf = workflows[0];
    const hasDetails = !!firstWf.details && Object.keys(firstWf.details).length > 0;
    this.fetchByDetailsAvailability(
      hasDetails,
      hasDetails ? firstWf.id : firstWf.flow_template_id,
      targetLevelNodes[0]?.index ?? 0,
      0
    );
  }

  private initLevelsState(
    containerId: number,
    parentLevel: number,
    targetLevel: number
  ): void {
    if (containerId && parentLevel > 0) {
      if (!this.levels().includes(targetLevel)) {
        this.levels.set([...this.levels(), targetLevel]);
      }
    } else {
      this.levels.set([0]);
    }
  }

  private resolvePage(levelKey: string, loadMore: boolean): number {
    const currentPages = this.sequentialCurrentPages();
    const page = loadMore ? (currentPages[levelKey] || 1) + 1 : 1;
    if (!loadMore) {
      this.sequentialCurrentPages.set({ ...currentPages, [levelKey]: 1 });
    }
    return page;
  }

  private setFlowDetails(res: RequestDetailsResponse): void {
    this.flowId.set(res.data.id);
    this.requestDetailsResponse.set({
      ...res,
      data: {
        ...res.data,
        positions: res.data.positions?.map(position => ({
          ...position,
          state: this.requestService.getPositionState(position),
        })),
      },
    });
  }
}
